import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Button,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { TranslucentContainer } from '../../../../../components';
import { APP_ROUTES } from '../../../../../constants/app-routes';
import { useStorageImage } from '../../../../../hooks/useStorageImage';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import DefaultPhoto from '../../../../../assets/img/default-img.png';
import * as EventActions from '../../../../../core/events/action';
import CardButtons from '../../components/CardButtons';
import ShareEventButton from '../../../../../components/ShareEvent/ShareEventButton';
import ShareEventModal from '../../../../../components/ShareEvent/ShareEventModal';

function VenueCalendarView({ events }) {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const CustomizedSwal = withReactContent(Swal);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const eventDetailsPanelRef = useRef(null);

  // Load photo for selected event
  const selectedEventPhoto = useStorageImage(selectedEvent?.photo);

  // Helper function to convert 24-hour format to 12-hour format with AM/PM
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return '';
    // Parse the time string (e.g., "14:30" or "14:30:00")
    const time = moment(timeString, 'HH:mm:ss').isValid()
      ? moment(timeString, 'HH:mm:ss')
      : moment(timeString, 'HH:mm');
    // Return formatted time with AM/PM
    return time.isValid() ? time.format('h:mm A') : timeString;
  };

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  // Get all days for the current month view
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    const days = [];
    const day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  }, [currentMonth]);

  // Get events for a specific date
  const getEventsForDate = (date) =>
    events.filter((event) => {
      const eventDate = moment(event.parameters_event?.event_date).format('YYYY-MM-DD');
      return date.format('YYYY-MM-DD') === eventDate;
    });

  // Check if date is in current month
  const isCurrentMonth = (date) => date.month() === currentMonth.month();

  // Check if date is in the past
  const isPastDate = (date) => date.isBefore(moment(), 'day');

  // Check if date is today
  const isToday = (date) => date.isSame(moment(), 'day');

  // Handle event selection
  const handleEventClick = (event) => {
    setSelectedEvent(event);

    if (isSmallScreen && eventDetailsPanelRef.current) {
      setTimeout(() => {
        eventDetailsPanelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  };

  // Handle closing the event panel
  const handleCloseEventPanel = () => {
    setSelectedEvent(null);
    setIsShareModalOpen(false);
  };

  // Navigate to event details
  const navigateToDetails = () => {
    window.scrollTo(0, 0);
    navigate(`/app/event/${selectedEvent.id}`);
  };

  // Handle opening share modal
  const handleOpenShareModal = () => {
    setIsShareModalOpen(true);
  };

  // Handle closing share modal
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  const getModalFailed = (name, description) => {
    CustomizedSwal.fire({
      title: <strong>{name}</strong>,
      text: description,
      icon: 'error',
    });
  };

  const onHandlerActions = (event, buttonType, eventId) => {
    const eventSearch = events.find((i) => i.id === eventId);
    switch (buttonType) {
      // Update Event
      case 'primary': {
        dispatch(EventActions.resetUpdateEventForm());
        const updatePage = APP_ROUTES.UPDATE_EVENT.replace(':id', eventId);
        window.scrollTo(0, 0);
        navigate(updatePage, { state: { event: eventSearch } });
        break;
      }
      // Cancel Event
      case 'secondary': {
        const { event_date: eventDate, hour_start_event: hourStartEvent } =
          eventSearch.parameters_event;
        const compareDate = moment(`${moment(eventDate).format('YYYY/MM/DD')} ${hourStartEvent}`);
        const currentDate = moment();
        const diffHours = compareDate.diff(currentDate, 'hours');

        if (diffHours > 48) dispatch(EventActions.onCancelEvent(eventId));
        else if (diffHours < 0)
          getModalFailed('Event cancellation failed', 'You can not cancel a past event');
        else
          getModalFailed(
            'Event cancellation failed',
            'It is not possible to cancel this event 48 hours before the event',
          );
        break;
      }
      default:
        break;
    }
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get events for the current month, sorted by date
  const eventsThisMonth = useMemo(
    () =>
      events
        .filter((event) => {
          const eventDate = moment(event.parameters_event?.event_date);
          return eventDate.isSame(currentMonth, 'month');
        })
        .sort((a, b) => {
          const dateA = moment(a.parameters_event?.event_date);
          const dateB = moment(b.parameters_event?.event_date);
          return dateA.diff(dateB);
        }),
    [events, currentMonth],
  );

  // Mobile Agenda View
  if (isMobile) {
    return (
      <Grid container marginTop={5} justifyContent="center" spacing={3}>
        <Grid item xs={12}>
          <TranslucentContainer>
            <Box sx={{ padding: 3 }}>
              {/* Month Navigation */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={3}>
                <IconButton onClick={previousMonth} sx={{ color: '#EE7B57' }}>
                  <ChevronLeft />
                </IconButton>
                <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                  {currentMonth.format('MMMM YYYY')}
                </Typography>
                <IconButton onClick={nextMonth} sx={{ color: '#EE7B57' }}>
                  <ChevronRight />
                </IconButton>
              </Box>

              {/* Agenda List */}
              {eventsThisMonth.length > 0 ? (
                <Box>
                  {eventsThisMonth.map((event) => (
                    <Card
                      key={event.id}
                      sx={{
                        marginBottom: 2,
                        borderLeft: '4px solid #EE7B57',
                        '&:hover': { boxShadow: 3 },
                      }}
                      onClick={() => handleEventClick(event)}>
                      <CardContent>
                        <Typography variant="h6" color="#EE7B57" gutterBottom>
                          {event.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          📅 {moment(event.parameters_event?.event_date).format('dddd, MMMM Do')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          🕒 {formatTimeTo12Hour(event?.parameters_event?.hour_start_event)} -{' '}
                          {formatTimeTo12Hour(event?.parameters_event?.hour_end_event)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" padding={4}>
                  <Typography variant="body1" color="text.secondary">
                    No events found for {currentMonth.format('MMMM YYYY')}
                  </Typography>
                </Box>
              )}
            </Box>
          </TranslucentContainer>
        </Grid>

        {/* Event Details Panel for Mobile */}
        {selectedEvent && (
          <Grid item xs={12} ref={eventDetailsPanelRef}>
            <TranslucentContainer>
              <Box sx={{ padding: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom={2}>
                  <Typography variant="h6" color="#EE7B57" fontWeight="bold">
                    Event Details
                  </Typography>
                  <IconButton
                    onClick={handleCloseEventPanel}
                    size="small"
                    sx={{
                      color: '#EE7B57',
                      '&:hover': { backgroundColor: 'rgba(238, 123, 87, 0.1)' },
                    }}>
                    <Close />
                  </IconButton>
                </Box>

                <Box
                  component="img"
                  src={selectedEventPhoto || DefaultPhoto}
                  alt={selectedEvent.name}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 2,
                    marginBottom: 2,
                  }}
                />

                <Typography variant="h5" color="#EE7B57" fontWeight="bold" gutterBottom>
                  {selectedEvent.name}
                </Typography>

                <Box marginBottom={2}>
                  <Typography variant="body1" color="text.primary" fontWeight="bold">
                    📅 {moment(selectedEvent.parameters_event?.event_date).format('MMMM Do, YYYY')}
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    🕒 {formatTimeTo12Hour(selectedEvent?.parameters_event?.hour_start_event)} -{' '}
                    {formatTimeTo12Hour(selectedEvent?.parameters_event?.hour_end_event)}
                  </Typography>
                </Box>

                {selectedEvent.description && (
                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {selectedEvent.description}
                    </Typography>
                  </Box>
                )}

                <Box marginTop={3}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => onHandlerActions(null, 'primary', selectedEvent.id)}
                        sx={{
                          borderColor: '#EE7B57',
                          color: '#EE7B57',
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#d66a47',
                            backgroundColor: 'rgba(238, 123, 87, 0.1)',
                          },
                        }}>
                        Edit
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onHandlerActions(null, 'secondary', selectedEvent.id)}
                        sx={{
                          backgroundColor: '#EE7B57',
                          color: 'white',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#d66a47',
                          },
                        }}>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={navigateToDetails}
                        sx={{
                          borderColor: '#EE7B57',
                          color: '#EE7B57',
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#d66a47',
                            backgroundColor: 'rgba(238, 123, 87, 0.1)',
                          },
                        }}>
                        Details
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleOpenShareModal}
                        sx={{
                          borderColor: '#EE7B57',
                          color: '#EE7B57',
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#d66a47',
                            backgroundColor: 'rgba(238, 123, 87, 0.1)',
                          },
                        }}>
                        Share Event
                      </Button>
                    </Grid>
                  </Grid>
                  <ShareEventModal
                    open={isShareModalOpen}
                    handleClose={handleCloseShareModal}
                    eventId={selectedEvent.id}
                  />
                </Box>
              </Box>
            </TranslucentContainer>
          </Grid>
        )}
      </Grid>
    );
  }

  // Desktop Calendar View
  return (
    <Grid container marginTop={5} justifyContent="center" spacing={3}>
      {/* Calendar */}
      <Grid item xs={12} lg={selectedEvent ? 8 : 12}>
        <TranslucentContainer>
          <Box sx={{ padding: 3 }}>
            {/* Month Navigation */}
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
              <IconButton onClick={previousMonth} sx={{ color: '#EE7B57' }}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                {currentMonth.format('MMMM YYYY')}
              </Typography>
              <IconButton onClick={nextMonth} sx={{ color: '#EE7B57' }}>
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Weekday Headers */}
            <Grid container spacing={0} sx={{ marginBottom: 1 }}>
              {weekDays.map((day) => (
                <Grid item xs={12 / 7} key={day} sx={{ textAlign: 'center', padding: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="#666">
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Days */}
            <Grid container spacing={0}>
              {calendarDays.map((day) => {
                const dayEvents = getEventsForDate(day);
                const isCurrentMonthDay = isCurrentMonth(day);
                const isPast = isPastDate(day);
                const isTodayDate = isToday(day);

                // Determine text color
                let textColor = '#CCC';
                if (isPast) {
                  textColor = '#999';
                } else if (isCurrentMonthDay) {
                  textColor = '#333';
                }

                return (
                  <Grid
                    item
                    xs={12 / 7}
                    key={day.format('YYYY-MM-DD')}
                    sx={{
                      padding: 0.5,
                      minHeight: 100,
                    }}>
                    <Box
                      sx={{
                        height: '100%',
                        minHeight: 100,
                        border: isTodayDate ? '2px solid #EE7B57' : '1px solid #E0E0E0',
                        borderRadius: 1,
                        padding: 1,
                        bgcolor: !isCurrentMonthDay ? '#F5F5F5' : 'white',
                        opacity: !isCurrentMonthDay ? 0.5 : 1,
                        '&:hover': {
                          bgcolor: isCurrentMonthDay ? 'rgba(238, 123, 87, 0.05)' : undefined,
                        },
                      }}>
                      <Typography
                        variant="body2"
                        fontWeight={isTodayDate ? 'bold' : 'normal'}
                        color={textColor}
                        sx={{ marginBottom: 0.5 }}>
                        {day.format('D')}
                      </Typography>

                      {/* Event indicators */}
                      {dayEvents.map((event) => (
                        <Box
                          key={event.id}
                          onClick={() => handleEventClick(event)}
                          sx={{
                            fontSize: '0.7rem',
                            padding: '2px 4px',
                            marginBottom: '2px',
                            bgcolor: '#EE7B57',
                            color: 'white',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              bgcolor: '#d66a47',
                            },
                          }}>
                          {event.name}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </TranslucentContainer>
      </Grid>

      {/* Event Details Panel */}
      {selectedEvent && (
        <Grid item xs={12} lg={4}>
          <TranslucentContainer>
            <Box sx={{ padding: 3, height: '100%' }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={2}>
                <Typography variant="h6" color="#EE7B57" fontWeight="bold">
                  Event Details
                </Typography>
                <IconButton
                  onClick={handleCloseEventPanel}
                  size="small"
                  sx={{
                    color: '#EE7B57',
                    '&:hover': { backgroundColor: 'rgba(238, 123, 87, 0.1)' },
                  }}>
                  <Close />
                </IconButton>
              </Box>

              <Box
                component="img"
                src={selectedEventPhoto || DefaultPhoto}
                alt={selectedEvent.name}
                sx={{
                  width: '100%',
                  height: 150,
                  objectFit: 'cover',
                  borderRadius: 2,
                  marginBottom: 2,
                }}
              />

              <Typography variant="h6" color="#EE7B57" fontWeight="bold" gutterBottom>
                {selectedEvent.name}
              </Typography>

              <Box marginBottom={2}>
                <Typography variant="body2" color="text.primary" fontWeight="bold">
                  📅 {moment(selectedEvent.parameters_event?.event_date).format('MMMM Do, YYYY')}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  🕒 {formatTimeTo12Hour(selectedEvent?.parameters_event?.hour_start_event)} -{' '}
                  {formatTimeTo12Hour(selectedEvent?.parameters_event?.hour_end_event)}
                </Typography>
              </Box>

              {selectedEvent.street_address && (
                <Box marginBottom={2}>
                  <Typography variant="body2" color="text.primary" fontWeight="bold">
                    📍 Address
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedEvent.street_address}
                  </Typography>
                </Box>
              )}

              {selectedEvent.description && (
                <Box marginBottom={2}>
                  <Typography variant="body2" color="text.primary" fontWeight="bold" gutterBottom>
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                    {selectedEvent.description}
                  </Typography>
                </Box>
              )}

              <Box marginTop={3}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={() => onHandlerActions(null, 'primary', selectedEvent.id)}
                      sx={{
                        borderColor: '#EE7B57',
                        color: '#EE7B57',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: '#d66a47',
                          backgroundColor: 'rgba(238, 123, 87, 0.1)',
                        },
                      }}>
                      Edit
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="small"
                      onClick={() => onHandlerActions(null, 'secondary', selectedEvent.id)}
                      sx={{
                        backgroundColor: '#EE7B57',
                        color: 'white',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: '#d66a47',
                        },
                      }}>
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={navigateToDetails}
                      sx={{
                        borderColor: '#EE7B57',
                        color: '#EE7B57',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: '#d66a47',
                          backgroundColor: 'rgba(238, 123, 87, 0.1)',
                        },
                      }}>
                      Details
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={handleOpenShareModal}
                      sx={{
                        borderColor: '#EE7B57',
                        color: '#EE7B57',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: '#d66a47',
                          backgroundColor: 'rgba(238, 123, 87, 0.1)',
                        },
                      }}>
                      Share Event
                    </Button>
                  </Grid>
                </Grid>
                <ShareEventModal
                  open={isShareModalOpen}
                  handleClose={handleCloseShareModal}
                  eventId={selectedEvent.id}
                />
              </Box>
            </Box>
          </TranslucentContainer>
        </Grid>
      )}
    </Grid>
  );
}

VenueCalendarView.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      photo: PropTypes.string,
      street_address: PropTypes.string,
      parameters_event: PropTypes.shape({
        event_date: PropTypes.string,
        hour_start_event: PropTypes.string,
        hour_end_event: PropTypes.string,
      }),
    }),
  ).isRequired,
};

export default VenueCalendarView;
